import type { CurrencyCode } from "@contract/standards/formats";
import {
  CALENDAR_YEAR_OPTIONS,
  CURRENCY_OPTIONS,
  FISCAL_STYLE_OPTIONS,
  MONTH_OPTIONS,
  NOT_SET_OPTION,
  currencyStyleOptions,
} from "@contract/standards/selectOptions";
import { Select, SimpleGrid } from "@mantine/core";
import { PANEL_SPACE } from "../../ui/tokens";
import type { StandardsDraft } from "../standardsModel";

/** A select's value: "" (the draft's Not set) shows as the Not set option. */
export const shown = (value: string) => (value === "" ? NOT_SET_OPTION.value : value);
export const stored = (value: string | null) => (!value || value === NOT_SET_OPTION.value ? "" : value);

/**
 * The label standards as closed selects (standards selects spec §6): currency and how it's
 * written, calendar year, fiscal year and its end month. Shared by Brand Configuration's
 * Labels card and the onboarding wizard; `strongLabels` sets the labels semibold (the
 * onboarding frames, Dan 2026-10-02).
 */
export function LabelsFields({
  draft,
  patch,
  readOnly = false,
  strongLabels = false,
}: {
  draft: StandardsDraft;
  patch: (partial: Partial<StandardsDraft>) => void;
  readOnly?: boolean;
  strongLabels?: boolean;
}) {
  const currency = draft.currency === "" ? null : (draft.currency as CurrencyCode);
  const styles = strongLabels ? { label: { fontWeight: 600 } } : undefined;
  return (
    <>
      {/* Currency + "Written As" (selects spec §3.1, D-1): Not set clears both; the examples
          follow the chosen currency. */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={PANEL_SPACE.card}>
        <Select
          styles={styles}
          label="Currency"
          data={[NOT_SET_OPTION].concat(CURRENCY_OPTIONS)}
          value={shown(draft.currency)}
          onChange={(v) => patch(stored(v) ? { currency: stored(v) } : { currency: "", currencyStyle: "" })}
          allowDeselect={false}
          searchable
          readOnly={readOnly}
        />
        <Select
          styles={styles}
          label="Written As"
          placeholder={currency ? "Choose how amounts are written" : "Not set"}
          data={currencyStyleOptions(currency)}
          value={draft.currencyStyle === "" ? null : draft.currencyStyle}
          onChange={(v) => patch({ currencyStyle: stored(v) })}
          allowDeselect={false}
          disabled={!readOnly && currency === null}
          readOnly={readOnly}
        />
      </SimpleGrid>
      <Select
        styles={styles}
        label="Calendar Year Format"
        data={[NOT_SET_OPTION].concat(CALENDAR_YEAR_OPTIONS)}
        value={shown(draft.calendarYearFormat)}
        onChange={(v) => patch({ calendarYearFormat: stored(v) })}
        allowDeselect={false}
        readOnly={readOnly}
      />
      {/* Fiscal year format + the optional end month (selects spec §3.3, D-2): Not set on the
          format clears the month too. */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={PANEL_SPACE.card}>
        <Select
          styles={styles}
          label="Fiscal Year Format"
          data={[NOT_SET_OPTION].concat(FISCAL_STYLE_OPTIONS)}
          value={shown(draft.fiscalStyle)}
          onChange={(v) => patch(stored(v) ? { fiscalStyle: stored(v) } : { fiscalStyle: "", fiscalEndMonth: "" })}
          allowDeselect={false}
          readOnly={readOnly}
        />
        <Select
          styles={styles}
          label="Fiscal Year Ends In"
          data={[NOT_SET_OPTION].concat(MONTH_OPTIONS)}
          value={shown(draft.fiscalEndMonth)}
          onChange={(v) => patch({ fiscalEndMonth: stored(v) })}
          allowDeselect={false}
          disabled={!readOnly && draft.fiscalStyle === ""}
          readOnly={readOnly}
        />
      </SimpleGrid>
    </>
  );
}
